import { paymentOptionSelectionSelectors } from 'ducks/paymentOptionSelection'
import ShowcaseModal from 'expo/showcase/ShowcaseModal'
import { useGetSoldRecords } from 'projectSections/sections/payments/cashFlowTransactions/utils'
import { useIsProjectUsingCashFlow } from 'projectSections/sections/payments/hooks'
import { useNotify, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'util/featureFlags'
import AboutUsDialog from '../../dialogs/AboutUsDialog'
import CheckoutFinancingDialog from '../../selectionComponent/checkoutFinancing/CheckoutFinancingDialog'
import { CaseStudiesDialog } from '../../dialogs/CaseStudiesDialog'
import DailyEnergyFlowsDialog from '../../dialogs/DailyEnergyFlowsDialog'
import DailyEnergyFlowsDialogSolarZero from '../../dialogs/DailyEnergyFlowsDialogSolarZero'
import FinancialDetailDialog from '../../dialogs/FinancialDetailDialog'
import { IframeDialog } from '../../dialogs/IframeDialog'
import LoadingDialog from '../../dialogs/LoadingDialog'
import NextStepsDialog from '../../dialogs/NextStepsDialog'
import SavingsDetailDialog from '../../dialogs/SavingsDetailDialog'
import SystemDetailDialog from '../../dialogs/SystemDetailDialog'
import VideoDialog from '../../dialogs/VideoDialog'
import EditUsage from '../../EditUsage'
import GenerateProposal from '../../GenerateProposal'
import ProposalV2Dialogs from './proposalV2/ProposalV2Dialogs'

const mergeFirstNumbersInArray = (arr) => [arr[0] + arr[1]].concat(arr.slice(2))
const DIALOGS_REQUIRE_FULL_CALC = ['GenerateProposal', 'DailyEnergyFlowsDialog', 'ImageDialog', 'FinancialDetail']

export const RenderDialog = (props) => {
  const { proposalData, closeDialog, activeDialog, dialogData, layout, user, dialogIsFullWidth, showLetter } = props
  const translate = useTranslate()
  const notify = useNotify()
  const myeStyles = proposalData.myeStyles

  const [soldSystem, soldPaymentOption] = useGetSoldRecords(proposalData.selectedProject)
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)
  // Pass proposalData.systems explicitly so that studio-hidden customer flows
  // (use_prerendered_system_image) don't fall back to an empty window.editor.getSystems().
  const isProjectUsingCashflow = useIsProjectUsingCashFlow(
    soldSystem,
    soldPaymentOption,
    refreshSystemsTrigger,
    proposalData.systems
  )
  const expoEnabled = useFeatureFlag('expo', 'on')
  const isExpoHookEnabled = useFeatureFlag('expo_hook', 'on')

  if (!proposalData.fullCalcReady && DIALOGS_REQUIRE_FULL_CALC.includes(activeDialog)) {
    return <LoadingDialog closeDialog={closeDialog} />
  }

  switch (activeDialog) {
    case 'AboutUs':
      return (
        <AboutUsDialog
          org={proposalData.org}
          title={proposalData.proposalTemplateSettings.heading_cover}
          content={props.content}
          showLetter={showLetter}
          isOpen={true}
          closeDialog={closeDialog}
          selectedProject={proposalData.selectedProject}
          myeStyles={myeStyles}
        />
      )
    case 'NextSteps':
      return (
        <NextStepsDialog
          isOpen={true}
          closeDialog={closeDialog}
          myeStyles={myeStyles}
          content={
            proposalData.selectedProject &&
            proposalData.selectedProject.proposal_data &&
            proposalData.selectedProject.proposal_data.proposal_template_settings &&
            proposalData.selectedProject.proposal_data.proposal_template_settings.next_steps_content
          }
        />
      )
    case 'CaseStudies':
      return (
        <CaseStudiesDialog
          org={proposalData.org}
          isOpen={true}
          testimonials={proposalData.selectedProject.testimonials}
          proposalData={proposalData}
          closeDialog={closeDialog}
          myeStyles={myeStyles}
        />
      )
    case 'EditUsage':
      return (
        <EditUsage
          isOpen={true}
          selectedProject={proposalData.selectedProject}
          closeDialog={closeDialog}
          resource={'my_projects'}
          pathname={'/myenergy/' + proposalData.selectedProject.id}
          id={proposalData.selectedProject.id}
        />
      )
    case 'DailyEnergyFlowsDialog':
      return (
        <DailyEnergyFlowsDialog
          isOpen={true}
          system={proposalData.selectedSystem}
          hemisphere={proposalData.selectedProject.lat > 0 ? 'north' : 'south'}
          closeDialog={closeDialog}
          layout={layout}
          myeStyles={myeStyles}
        />
      )
    case 'ImageDialog':
      return (
        <DailyEnergyFlowsDialogSolarZero
          isOpen={true}
          closeDialog={closeDialog}
          title={proposalData.proposalTemplateSettings.heading_daily_energy_flows}
        />
      )
    case 'Video':
      return <VideoDialog isOpen={true} closeDialog={closeDialog} myeStyles={myeStyles} dialogData={dialogData} />
    case 'Detail':
      if (expoEnabled && isExpoHookEnabled)
        return <ShowcaseModal open={true} onClose={closeDialog} component={dialogData.component} />
      return (
        <SystemDetailDialog
          isOpen={true}
          closeDialog={closeDialog}
          myeStyles={myeStyles}
          dialogData={dialogData}
          system={proposalData.selectedSystem}
        />
      )
    case 'FinancialDetail':
      return (
        <FinancialDetailDialog
          isOpen={true}
          cashFlows={mergeFirstNumbersInArray(props.proposalData.cashFlows['cash_flows'])}
          cumulativeImpacts={props.proposalData.cashFlows['bank_balance'].slice(1)}
          closeDialog={closeDialog}
          proposalData={proposalData}
          myeStyles={myeStyles}
          translate={translate}
        />
      )
    case 'SavingsDetail':
      return (
        <SavingsDetailDialog
          isOpen={true}
          closeDialog={closeDialog}
          proposalData={proposalData}
          myeStyles={myeStyles}
          translate={translate}
        />
      )
    case 'GenerateProposal':
      if (!proposalData.fullCalcReady && DIALOGS_REQUIRE_FULL_CALC.includes(activeDialog)) {
        return <LoadingDialog closeDialog={closeDialog} />
      } else {
        return (
          <GenerateProposal
            translate={translate}
            notify={notify}
            key={
              proposalData.selectedSystem.uuid +
              '_' +
              proposalData.selectedPaymentOption.id +
              '_' +
              proposalData.selectedViewUuid
            }
            isOpen={true}
            user={user}
            fullCalcReady={proposalData.fullCalcReady}
            selectedSystem={proposalData.selectedSystem}
            proposalTemplateSettings={proposalData.proposalTemplateSettings}
            systems={proposalData.systems}
            selectedProject={proposalData.selectedProject}
            selectedOrg={proposalData.org}
            selectedPaymentOption={proposalData.selectedPaymentOption}
            hasCashFlow={isProjectUsingCashflow}
            selectedViewUuid={proposalData.selectedViewUuid}
            closeDialog={closeDialog}
            myeStyles={myeStyles}
          />
        )
      }
    case 'ProposalV2Dialogs':
      return (
        <ProposalV2Dialogs
          isOpen={true}
          closeDialog={closeDialog}
          dialogData={dialogData}
          proposalData={proposalData}
          fetchProjectData={props.fetchProjectData}
        />
      )
    case 'CheckoutFinancing': {
      const po = proposalData.selectedPaymentOption
      const proj = proposalData.selectedProject
      const sys = proposalData.selectedSystem
      if (!po?.id || !proj?.id || !proj.country_iso2 || !sys?.uuid) {
        return <LoadingDialog closeDialog={closeDialog} />
      }
      const amountDue = po.pricing?.system_price_payable ?? po.pricing?.system_price_including_tax ?? 0
      return (
        <CheckoutFinancingDialog
          open
          onClose={closeDialog}
          orgId={proposalData.org.id}
          projectId={proj.id}
          countryIso2={proj.country_iso2}
          systemUuid={sys.uuid}
          paymentOptionId={po.id}
          expectedMilestonePayments={po.expected_milestone_payments}
          amountDue={amountDue}
        />
      )
    }
    default:
      return (
        <IframeDialog
          activeDialog={activeDialog}
          closeDialog={closeDialog}
          myeStyles={myeStyles}
          dialogData={dialogData}
          dialogIsFullWidth={dialogIsFullWidth}
        />
      )
  }
}

export default RenderDialog
